import { useCallback, useEffect, useState } from 'react';
import { Plus, Edit2, X, Loader2, UserPlus } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface DependentRow {
  contact_id: string;
  matter_id: string;
  role: string;
  is_migrating: boolean;
  relationship: string | null;
  added_at: string;
  contact: {
    id: string;
    given_names: string | null;
    family_name: string | null;
    date_of_birth: string | null;
    passport_number: string | null;
    passport_country: string | null;
    passport_expiry: string | null;
  } | null;
}

interface Props {
  matterId: string;
  isStaff: boolean;
}

export default function MatterDependentsPanel({ matterId, isStaff }: Props) {
  const [rows, setRows] = useState<DependentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<DependentRow | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('matter_contacts')
      .select(
        `contact_id, matter_id, role, is_migrating, relationship, added_at,
         contact:contacts!matter_contacts_contact_id_fkey (id, given_names, family_name, date_of_birth, passport_number, passport_country, passport_expiry)`,
      )
      .eq('matter_id', matterId)
      .eq('role', 'dependant')
      .order('added_at', { ascending: true });
    if (err) {
      setError(err.message);
    } else {
      setRows((data ?? []) as unknown as DependentRow[]);
      setError(null);
    }
    setLoading(false);
  }, [matterId]);

  useEffect(() => {
    if (!matterId) return;
    refresh();
  }, [matterId, refresh]);

  return (
    <>
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100">
          <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
            Migrating dependents
          </h3>
          {isStaff && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="text-xs text-[#2CABEA] hover:underline inline-flex items-center gap-1"
            >
              <Plus size={11} /> Add
            </button>
          )}
        </div>

        <div className="p-3 space-y-2">
          {loading ? (
            <div className="text-xs text-slate-500 py-1">Loading dependents...</div>
          ) : error ? (
            <div className="text-xs text-red-600">{error}</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-slate-500 inline-flex items-center gap-2">
              <UserPlus size={12} className="text-slate-400" />
              {isStaff
                ? 'No dependents on this matter. Add children, step-children, or aged dependants migrating with the applicant.'
                : 'No dependents on this matter.'}
            </div>
          ) : (
            rows.map((row) => (
              <DependentRowDisplay
                key={row.contact_id}
                row={row}
                isStaff={isStaff}
                onEdit={() => setEditing(row)}
              />
            ))
          )}
        </div>
      </div>

      {(adding || editing) && (
        <DependentEditModal
          matterId={matterId}
          row={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => {
            setAdding(false);
            setEditing(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function DependentRowDisplay({
  row,
  isStaff,
  onEdit,
}: {
  row: DependentRow;
  isStaff: boolean;
  onEdit: () => void;
}) {
  const c = row.contact;
  const name = [c?.given_names, c?.family_name].filter(Boolean).join(' ').trim() || 'Unnamed dependent';
  const age = c?.date_of_birth ? ageInYears(c.date_of_birth) : null;
  const isMinor = age !== null && age < 18;
  return (
    <div className="flex items-start gap-2">
      <div className="flex-1 min-w-0 text-sm">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-semibold text-[#0B2341]">{name}</span>
          {age !== null && (
            <span className="text-xs text-slate-500">
              {age} year{age === 1 ? '' : 's'} old
            </span>
          )}
          {isMinor && (
            <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-700">
              Minor - Form 1229 needed
            </span>
          )}
          {!row.is_migrating && (
            <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">
              Not migrating
            </span>
          )}
        </div>
        <div className="text-xs text-slate-600 mt-0.5">
          {row.relationship ?? 'Relationship not set'}
          {c?.passport_number && ` · Passport ${c.passport_number}${c.passport_country ? ` (${c.passport_country})` : ''}`}
        </div>
      </div>
      {isStaff && (
        <button
          type="button"
          onClick={onEdit}
          className="text-slate-400 hover:text-[#2CABEA] p-1"
          title="Edit"
        >
          <Edit2 size={12} />
        </button>
      )}
    </div>
  );
}

function ageInYears(dob: string): number {
  const birth = new Date(dob);
  const today = new Date();
  let years = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    years--;
  }
  return years;
}

// ============================================================
// Add / edit modal
// ============================================================

const RELATIONSHIP_OPTIONS = [
  'Child of applicant',
  'Step-child of applicant',
  'Adopted child of applicant',
  'Aged dependant relative',
  'Other',
];

function DependentEditModal({
  matterId,
  row,
  onClose,
  onSaved,
}: {
  matterId: string;
  row: DependentRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = Boolean(row);
  const c = row?.contact;
  const [givenNames, setGivenNames] = useState(c?.given_names ?? '');
  const [familyName, setFamilyName] = useState(c?.family_name ?? '');
  const [dob, setDob] = useState(c?.date_of_birth ?? '');
  const [passportNumber, setPassportNumber] = useState(c?.passport_number ?? '');
  const [passportCountry, setPassportCountry] = useState(c?.passport_country ?? '');
  const [passportExpiry, setPassportExpiry] = useState(c?.passport_expiry ?? '');
  const [relationship, setRelationship] = useState(row?.relationship ?? RELATIONSHIP_OPTIONS[0]);
  const [isMigrating, setIsMigrating] = useState(row?.is_migrating ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!givenNames.trim() || !familyName.trim()) {
      setError('Given names and family name are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let contactId = c?.id;
      const contactPayload = {
        given_names: givenNames.trim(),
        family_name: familyName.trim(),
        date_of_birth: dob || null,
        passport_number: passportNumber.trim() || null,
        passport_country: passportCountry.trim() || null,
        passport_expiry: passportExpiry || null,
        contact_type: 'dependant',
        email: null,
      };

      if (isEdit && contactId) {
        const { error: cErr } = await supabase
          .from('contacts')
          .update(contactPayload)
          .eq('id', contactId);
        if (cErr) throw cErr;

        const { error: mcErr } = await supabase
          .from('matter_contacts')
          .update({ relationship, is_migrating: isMigrating })
          .eq('matter_id', matterId)
          .eq('contact_id', contactId)
          .eq('role', 'dependant');
        if (mcErr) throw mcErr;
      } else {
        const { data: created, error: cErr } = await supabase
          .from('contacts')
          .insert(contactPayload)
          .select('id')
          .single();
        if (cErr) throw cErr;
        contactId = created.id;

        const { error: mcErr } = await supabase.from('matter_contacts').insert({
          matter_id: matterId,
          contact_id: contactId,
          role: 'dependant',
          relationship,
          is_migrating: isMigrating,
        });
        if (mcErr) throw mcErr;
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!c?.id) return;
    if (!confirm(`Remove ${[givenNames, familyName].filter(Boolean).join(' ').trim() || 'this dependent'} from the matter? This cannot be undone.`))
      return;
    setSaving(true);
    try {
      // Delete the matter_contacts link only — we keep the contact row in case
      // it's referenced elsewhere or needs to be re-linked later.
      const { error: err } = await supabase
        .from('matter_contacts')
        .delete()
        .eq('matter_id', matterId)
        .eq('contact_id', c.id)
        .eq('role', 'dependant');
      if (err) throw err;
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Remove failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h2 className="font-semibold text-[#0B2341]">
            {isEdit ? 'Edit dependent' : 'Add dependent'}
          </h2>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Given names *</label>
              <input
                type="text"
                value={givenNames}
                onChange={(e) => setGivenNames(e.target.value)}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Family name *</label>
              <input
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                className="form-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Date of birth</label>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                className="form-input"
              />
              {dob && (
                <p className="form-hint">
                  Age: {ageInYears(dob)} years.{' '}
                  {ageInYears(dob) < 18 && (
                    <span className="text-amber-700 font-medium">
                      Form 1229 (consent for minor) will be required.
                    </span>
                  )}
                </p>
              )}
            </div>
            <div>
              <label className="form-label">Relationship</label>
              <select
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                className="form-select"
              >
                {RELATIONSHIP_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="form-label">Passport number</label>
            <input
              type="text"
              value={passportNumber}
              onChange={(e) => setPassportNumber(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Passport country</label>
              <input
                type="text"
                value={passportCountry}
                onChange={(e) => setPassportCountry(e.target.value)}
                className="form-input"
                placeholder="e.g. India"
              />
            </div>
            <div>
              <label className="form-label">Passport expiry</label>
              <input
                type="date"
                value={passportExpiry}
                onChange={(e) => setPassportExpiry(e.target.value)}
                className="form-input"
              />
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isMigrating}
              onChange={(e) => setIsMigrating(e.target.checked)}
            />
            Migrating with the applicant on this visa
          </label>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-200 bg-slate-50">
          {isEdit ? (
            <button
              type="button"
              onClick={handleRemove}
              disabled={saving}
              className="text-xs text-red-600 hover:underline disabled:opacity-40"
            >
              Remove from matter
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="btn-secondary !py-1.5 text-sm">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn-primary !py-1.5 text-sm inline-flex items-center gap-1.5"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : null}
              {isEdit ? 'Save changes' : 'Add dependent'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
